import React from 'react'
import { Droppable } from 'react-beautiful-dnd'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Button,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import IdeaCard from './IdeaCard'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const RoadmapSection = ({
  title,
  helperText,
  horizon,
  ideas = [],
  onAdd,
  onIdeaClick,
  collapsible = false,
  defaultExpanded = true,
  droppableId,
}) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [expanded, setExpanded] = React.useState(defaultExpanded)

  const handleChange = (event, isExpanded) => {
    if (collapsible) {
      setExpanded(isExpanded)
    }
  }

  const content = (
    <Droppable droppableId={droppableId}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            minHeight: snapshot.isDraggingOver ? '100px' : '50px',
            backgroundColor: snapshot.isDraggingOver
              ? 'rgba(25, 118, 210, 0.1)'
              : 'transparent',
            borderRadius: '8px',
            p: snapshot.isDraggingOver ? 1 : 0,
            transition: 'all 0.2s ease',
          }}
        >
          {ideas.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              <Typography variant="body2">{t('roadmap.noIdeaYet')}</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 2,
              }}
            >
              {ideas.map((idea, index) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  index={index}
                  onClick={onIdeaClick}
                  horizon={horizon}
                />
              ))}
            </Box>
          )}
          {provided.placeholder}
        </Box>
      )}
    </Droppable>
  )

  if (collapsible) {
    return (
      <Accordion
        expanded={expanded}
        onChange={handleChange}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          mb: 2,
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 600,
                flex: 1,
                fontSize: { xs: '1.2rem', md: '1.4rem' },
              }}
            >
              {title}
            </Typography>
            {onAdd && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  onAdd()
                }}
                sx={{ color: 'white' }}
                size="small"
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {helperText && (
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                mb: 2,
                lineHeight: 1.75,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              {helperText}
            </Typography>
          )}
          {content}
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <Box
      sx={{
        mb: 3,
        backgroundColor: 'rgba(40, 40, 40, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        p: 3,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: '1.2rem', md: '1.4rem' },
            }}
          >
            {title}
          </Typography>
          {helperText && (
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: 1.75,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              {helperText}
            </Typography>
          )}
        </Box>
        {onAdd && (
          <IconButton onClick={onAdd} sx={{ color: 'white' }} size="small">
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {content}
    </Box>
  )
}

export default RoadmapSection

